import { injectable, inject } from 'inversify';
import { GetProductsAvailableInBranchUseCase } from '../../../domain/usecases/ProductUseCases';
import type { ProductRepository, BranchInventoryRepository } from '../../../domain/repositories';
import { Product, ProductWithStock } from '../../../domain/models';
import type { BranchInventory } from '../../../domain/models/BranchInventory';
import { TYPES } from '../../../di/types';
import { attachBranchStock, inventoryByProductId } from './branchProductStock';

/**
 * Global active catalog for POS, enriched with **branch-specific** stock.
 * Products without a BranchInventory row remain visible (stock = 0).
 */
@injectable()
export class GetProductsAvailableInBranchUseCaseImpl implements GetProductsAvailableInBranchUseCase {
  constructor(
    @inject(TYPES.ProductRepository) private readonly productRepository: ProductRepository,
    @inject(TYPES.BranchInventoryRepository) private readonly branchInventoryRepository: BranchInventoryRepository,
  ) {}

  async execute(branchId: string, page = 1, limit = 50): Promise<{
    products: ProductWithStock[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      this.validateParameters(branchId, page, limit);

      let inventoryMap = new Map<string, BranchInventory>();
      try {
        const branchInventories = await this.branchInventoryRepository.getByBranch(branchId);
        inventoryMap = inventoryByProductId(branchInventories);
      } catch (error) {
        console.warn('⚠️ Could not load branch inventory; catalog shown with stock 0', error);
      }

      const matched: Product[] = [];
      let catalogPage = 1;
      let catalogHasMore = true;
      let safety = 0;

      while (catalogHasMore && safety < 100) {
        const batch = await this.productRepository.findAll(branchId, catalogPage, 1000);
        for (const product of batch.products) {
          if (product.isActive) matched.push(product);
        }
        catalogHasMore = batch.hasMore && batch.products.length > 0;
        catalogPage += 1;
        safety += 1;
      }

      const productsWithStock = matched.map((product) =>
        attachBranchStock(product, branchId, inventoryMap.get(product.id) ?? null),
      );

      return {
        products: productsWithStock,
        total: productsWithStock.length,
        hasMore: false,
      };
    } catch (error) {
      console.error('Get products available in branch failed:', error);
      throw error;
    }
  }

  private validateParameters(branchId: string, page: number, limit: number): void {
    if (!branchId || branchId.trim().length === 0) {
      throw new Error('Branch ID is required');
    }
    if (page < 1) {
      throw new Error('Page number must be greater than 0');
    }
    if (limit < 1 || limit > 100) {
      throw new Error('Limit must be between 1 and 100');
    }
  }
}
